import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AI_PDF_CONST from "../../util/PdfAiConst";
import { fetchTenants } from "../../api/tenantApi";
import { fetchTenantUsers } from "../../api/tenantUserApi";
import PageHeader from "../../components/admin/PageHeader";
import LoadingState from "../../components/admin/LoadingState";
import ErrorState from "../../components/admin/ErrorState";
import StatusBadge from "../../components/admin/StatusBadge";

const TeamListPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const tenantRes = await fetchTenants();
        const tenantList = Array.isArray(tenantRes)
          ? tenantRes
          : tenantRes?.data?.items || tenantRes?.data || [];

        const enriched = await Promise.all(
          tenantList.map(async (tenant) => {
            let users = [];
            try {
              const userRes = await fetchTenantUsers(tenant.id);
              users = Array.isArray(userRes)
                ? userRes
                : userRes?.data?.items || userRes?.data || [];
            } catch {
              // skip
            }
            const active = users.filter((u) => u.status === "active").length;
            return {
              ...tenant,
              totalMembers: users.length,
              activeMembers: active,
            };
          })
        );
        setRows(enriched);
      } catch (err) {
        setError(err.message || "Failed to load team data.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) return <LoadingState label="Loading team data..." />;
  if (error) return <ErrorState description={error} />;

  const manageLink = (id) => AI_PDF_CONST.ROUTE.ADMIN_TENANT_DETAIL.replace(":tenant_id", id) + "?tab=team";

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Platform admin"
        title="Team management"
        description="Overview of team members across all tenants."
      />

      <div className="rounded-3xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50 text-left">
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Tenant Name</th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Total Members</th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Active Members</th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Admin Contact</th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Status</th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500 text-right">Quick Manage</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((tenant) => (
              <tr key={tenant.id} className="hover:bg-slate-50 transition-colors">
                <td className="px-5 py-4 font-medium text-slate-900">{tenant.name}</td>
                <td className="px-5 py-4 text-slate-700">{tenant.totalMembers}</td>
                <td className="px-5 py-4 text-slate-700">{tenant.activeMembers}</td>
                <td className="px-5 py-4 text-slate-500">{tenant.owner_name || "—"}</td>
                <td className="px-5 py-4"><StatusBadge status={tenant.status} /></td>
                <td className="px-5 py-4 text-right">
                  <Link
                    to={manageLink(tenant.id)}
                    className="inline-flex items-center rounded-lg bg-[#096dea] px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-[#0959bb]"
                  >
                    Manage →
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-400">No tenants found.</div>
        ) : null}
      </div>
    </div>
  );
};

export default TeamListPage;
